import { useEffect, useMemo, useState } from 'react'
import { Button } from '../../components/ui/Button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { apiGet, apiPostMultipart } from '../../lib/api.ts'

interface PlacementOption {
  id: number
  title: string
  practice_name: string
  status: string
  start_date: string
  submitted_dates: string[]
}

interface TimesheetEntry {
  id: number
  shift_date: string
  hours_worked: number
  break_minutes: number
}

interface Timesheet {
  id: number
  job_id: number
  shift_date: string
  period_start_date: string
  period_end_date: string
  entry_count: number
  hours_worked: number
  break_minutes: number
  notes: string
  status: string
  is_duplicate: boolean
  duplicate_count: number
  rejection_type: string
  rejection_category: string
  rejection_reason: string
  resubmission_of_timesheet_id: number
  submitted_at: string
  title: string
  practice_name: string
  entries: TimesheetEntry[]
}

interface TimesheetFormEntry {
  client_id: string
  shift_date: string
  hours_worked: string
}

function createEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `entry-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const emptyEntry = (): TimesheetFormEntry => ({
  client_id: createEntryId(),
  shift_date: '',
  hours_worked: '',
})

function formatDateLabel(value: string): string {
  if (!value) {
    return ''
  }

  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate) {
    return ''
  }

  if (!endDate || endDate === startDate) {
    return formatDateLabel(startDate)
  }

  return `${formatDateLabel(startDate)} to ${formatDateLabel(endDate)}`
}

export default function TimesheetsPage() {
  const [placementOptions, setPlacementOptions] = useState<PlacementOption[]>([])
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [awaitingApproval, setAwaitingApproval] = useState(0)
  const [duplicateWarning, setDuplicateWarning] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [form, setForm] = useState({
    job_id: '',
    timesheet_notes: '',
    resubmission_of_timesheet_id: '',
    entries: [emptyEntry()],
  })

  const selectedPlacement = useMemo(
    () => placementOptions.find((placement) => placement.id === Number(form.job_id)) ?? null,
    [placementOptions, form.job_id]
  )

  const totalHours = useMemo(
    () => form.entries.reduce((sum, entry) => sum + (Number(entry.hours_worked) || 0), 0),
    [form.entries]
  )

  const load = async () => {
    try {
      const data = await apiGet<{
        placement_options: PlacementOption[]
        timesheets: Timesheet[]
        awaiting_approval: number
        duplicate_timesheet_warning: string
      }>('/locum/timesheets.php?format=json')
      setPlacementOptions(data.placement_options)
      setTimesheets(data.timesheets)
      setAwaitingApproval(data.awaiting_approval)
      setDuplicateWarning(data.duplicate_timesheet_warning)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load timesheets.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const updateEntry = (index: number, key: keyof TimesheetFormEntry, value: string) => {
    setForm((current) => ({
      ...current,
      entries: current.entries.map((entry, entryIndex) => (
        entryIndex === index ? { ...entry, [key]: value } : entry
      )),
    }))
  }

  const addEntryRow = () => {
    setForm((current) => ({
      ...current,
      entries: [...current.entries, emptyEntry()],
    }))
  }

  const removeEntryRow = (index: number) => {
    setForm((current) => ({
      ...current,
      entries: current.entries.length <= 1
        ? current.entries
        : current.entries.filter((_, entryIndex) => entryIndex !== index),
    }))
  }

  const resetForm = () => {
    setForm({
      job_id: '',
      timesheet_notes: '',
      resubmission_of_timesheet_id: '',
      entries: [emptyEntry()],
    })
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setNotice('')

    const formData = new FormData()
    formData.append('action', 'submit_timesheet')
    formData.append('job_id', form.job_id)
    formData.append('timesheet_notes', form.timesheet_notes)
    formData.append('resubmission_of_timesheet_id', form.resubmission_of_timesheet_id)
    form.entries.forEach((entry) => {
      formData.append('entry_date[]', entry.shift_date)
      formData.append('entry_hours_worked[]', entry.hours_worked)
    })

    try {
      const data = await apiPostMultipart<{ success: boolean; message: string }>(
        '/locum/timesheets.php?format=json',
        formData
      )
      setNotice(data.message)
      resetForm()
      await load()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Unable to submit timesheet.')
    }
  }

  const useRejectedTimesheetForResubmission = (timesheet: Timesheet) => {
    const matchingPlacement = placementOptions.find((placement) => placement.id === timesheet.job_id)
    const fallbackPlacement = placementOptions.find(
      (placement) => placement.title === timesheet.title && placement.practice_name === timesheet.practice_name
    )
    setForm({
      job_id: matchingPlacement ? String(matchingPlacement.id) : fallbackPlacement ? String(fallbackPlacement.id) : '',
      timesheet_notes: timesheet.notes || '',
      resubmission_of_timesheet_id: String(timesheet.id),
      entries: timesheet.entries.length > 0
        ? timesheet.entries.map((entry) => ({
            client_id: createEntryId(),
            shift_date: entry.shift_date,
            hours_worked: String(entry.hours_worked),
          }))
        : [emptyEntry()],
    })
    setNotice(`Resubmission details loaded for ${timesheet.title || 'placement'}.`)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-2xl font-bold text-text-900">Timesheets</h1>
        <p className="mt-1 text-sm text-text-600">{awaitingApproval} awaiting practice approval</p>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{notice}</div>}
      {duplicateWarning && <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{duplicateWarning}</div>}

      <Card>
        <CardHeader>
          <CardTitle>Submit hours</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-text-600">
            Submit one or more worked dates for the same placement in a single timesheet.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <select
              value={form.job_id}
              onChange={(event) => setForm((current) => ({ ...current, job_id: event.target.value }))}
              className="w-full rounded-md border border-surface-600 px-3 py-3"
              required
            >
              <option value="">Select placement</option>
              {placementOptions.map((placement) => (
                <option key={placement.id} value={placement.id}>
                  {placement.title} - {placement.practice_name}
                </option>
              ))}
            </select>

            {selectedPlacement?.submitted_dates?.length ? (
              <div className="rounded-lg bg-surface-100 px-3 py-3 text-sm text-text-700">
                <p className="font-semibold text-text-900">Already submitted for this placement</p>
                <p className="mt-1">
                  {selectedPlacement.submitted_dates.map((date) => formatDateLabel(date)).join(', ')}
                </p>
              </div>
            ) : null}

            <div className="space-y-3">
              {form.entries.map((entry, index) => (
                <div key={entry.client_id} className="rounded-xl border border-surface-300 p-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-text-900">Day {index + 1}</p>
                    {form.entries.length > 1 ? (
                      <Button type="button" variant="outline" onClick={() => removeEntryRow(index)}>
                        Remove
                      </Button>
                    ) : null}
                  </div>
                  <div className="space-y-3">
                    <input
                      type="date"
                      value={entry.shift_date}
                      onChange={(event) => updateEntry(index, 'shift_date', event.target.value)}
                      lang="en-GB"
                      className="w-full rounded-md border border-surface-600 px-3 py-3"
                      required
                    />
                    <input
                      type="number"
                      step="0.25"
                      min="0.25"
                      placeholder="Hours worked"
                      value={entry.hours_worked}
                      onChange={(event) => updateEntry(index, 'hours_worked', event.target.value)}
                      className="w-full rounded-md border border-surface-600 px-3 py-3"
                      required
                    />
                  </div>
                </div>
              ))}
            </div>

            <Button type="button" variant="outline" className="w-full" onClick={addEntryRow}>
              Add another day
            </Button>

            <textarea
              rows={3}
              placeholder="Notes for the practice"
              value={form.timesheet_notes}
              onChange={(event) => setForm((current) => ({ ...current, timesheet_notes: event.target.value }))}
              className="w-full rounded-md border border-surface-600 px-3 py-3"
            />

            <div className="rounded-lg bg-surface-100 px-3 py-3 text-sm text-text-700">
              <p className="font-semibold text-text-900">Submission summary</p>
              <p className="mt-1">
                {form.entries.length} day{form.entries.length === 1 ? '' : 's'} · {totalHours.toFixed(2)} total hours
              </p>
            </div>

            <Button type="submit" className="w-full bg-brand-600 hover:bg-brand-700">
              Submit timesheet
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent submissions</CardTitle>
        </CardHeader>
        <CardContent>
          {timesheets.length === 0 ? (
            <p className="text-text-700">No timesheets submitted yet.</p>
          ) : (
            <div className="space-y-3">
              {timesheets.map((timesheet) => (
                <div key={timesheet.id} className="rounded-lg border border-surface-300 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-text-900">{timesheet.title || 'Placement'}</h3>
                      <p className="text-sm text-text-600">{timesheet.practice_name}</p>
                    </div>
                    <span className="rounded-full bg-surface-100 px-3 py-1 text-xs font-semibold capitalize text-text-700">
                      {timesheet.status}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-text-600">
                    <div>{formatDateRange(timesheet.period_start_date, timesheet.period_end_date)}</div>
                    <div>{timesheet.hours_worked.toFixed(2)} hrs total</div>
                    <div>{timesheet.entry_count} day{timesheet.entry_count === 1 ? '' : 's'}</div>
                    <div>Submitted {formatDateLabel(timesheet.submitted_at.slice(0, 10))}</div>
                  </div>
                  <div className="mt-3 rounded-lg bg-surface-50 px-3 py-3">
                    <div className="space-y-2 text-sm text-text-700">
                      {timesheet.entries.map((entry) => (
                        <div key={entry.id || `${timesheet.id}-${entry.shift_date}`} className="flex items-center justify-between gap-3">
                          <span>{formatDateLabel(entry.shift_date)}</span>
                          <span>{entry.hours_worked.toFixed(2)} hrs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {timesheet.notes ? (
                    <p className="mt-3 text-sm text-text-600">{timesheet.notes}</p>
                  ) : null}
                  {timesheet.rejection_reason && (
                    <div className={`mt-3 rounded-md px-3 py-2 text-sm ${timesheet.rejection_type === 'resubmit' ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                      <p className="font-semibold">
                        {timesheet.rejection_type === 'resubmit' ? 'Resubmission requested' : 'Rejected and removed'}
                      </p>
                      <p>
                        {timesheet.rejection_category ? `${timesheet.rejection_category}: ` : ''}
                        {timesheet.rejection_reason}
                      </p>
                    </div>
                  )}
                  {timesheet.is_duplicate && (
                    <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      Duplicate detected: {timesheet.duplicate_count} entries exist for one or more placement dates in this submission.
                    </div>
                  )}
                  {timesheet.status === 'resubmission_requested' && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => useRejectedTimesheetForResubmission(timesheet)}
                      >
                        Use details for resubmission
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
